import { Button, Card, Form, Input } from "antd";
import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { saveReviseRoutineApi } from "../../services/reviseRoutine.service";
import ReactGA from "react-ga4";
import { useNavigate } from "react-router-dom";

interface ReviseRoutineComponentProps {
  data?: any;
}

const { TextArea } = Input;

const ReviseRoutineComponent: React.FC<ReviseRoutineComponentProps> = ({
  data,
}) => {
  interface RoutineStatus {
    routine_status?: string;
    revision_time_remaining?: number;
  }

  interface Profile {
    first_name?: string;
    survey_id?: string;
    routine_status?: RoutineStatus;
  }

  const profile: Profile = useAppSelector((state) => state.profile);
  const { first_name, survey_id } = profile;

  const [selectedOption, setSelectedOption] = useState("");
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isAlreadyLocked, setIsAlreadyLocked] = useState(false);
  const [countdownEndTime, setCountdownEndTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    mins: 0,
    secs: 0,
  });
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [response, setResponse] = useState(data || null);
  const [showNextExpertReviewButton, setShowNextExpertReviewButton] =
    useState(false);

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const totalSeconds =
      timeRemaining.days * 24 * 60 * 60 +
      timeRemaining.hours * 60 * 60 +
      timeRemaining.mins * 60 +
      timeRemaining.secs;

    if (totalSeconds <= 0) {
      setIsLocked(false);
      // Timer expired - handle accordingly
    }
  }, [timeRemaining]);

  // Parse and initialize state based on API response
  useEffect(() => {
    if (!data) return;

    const {
      is_satisfied,
      routine_status: status,
      revision_time_remaining,
      show_next_expert_review_button,
      locked_at,
    } = data;
    setShowNextExpertReviewButton(!show_next_expert_review_button);
    setIsAlreadyLocked(show_next_expert_review_button);

    setSelectedOption(
      is_satisfied ? "yes" : is_satisfied === false ? "no" : ""
    );
    // Calculate countdown end time based on revision_time_remaining (in seconds)
    const now = new Date();
    const endTime = new Date(now.getTime() + revision_time_remaining * 1000);
    console.log("endTime", endTime);
    setCountdownEndTime(endTime);

    // Set locked state based on routine_status
    // "unlocked" = 14 days (unlocked)
    // "locked" = 90 days (locked)
    if (status === "locked") {
      setIsLocked(true);
    } else if (status === "unlocked") {
      setIsLocked(false);
    }
  }, [data]);

  useEffect(() => {
    if (isLocked) {
      if (selectedOption === "yes") {
        setInputValue(
          "Routine locked. We’ll follow this plan for 3 months to see how your skin responds."
        );
      } else if (selectedOption === "no") {
        setInputValue(
          "Expert is working on your change request. After this, your routine is locked. We’ll follow this plan for 3 months to see how your skin responds."
        );
      }
    } else {
      setInputValue("");
    }
  }, [isLocked, selectedOption]);

  // Timer update effect
  useEffect(() => {
    if (!countdownEndTime) return;

    const timer = setInterval(() => {
      updateTimeRemaining();
    }, 1000);

    return () => clearInterval(timer);
  }, [countdownEndTime]);

  const updateTimeRemaining = () => {
    if (!countdownEndTime) {
      setTimeRemaining({ days: 0, hours: 0, mins: 0, secs: 0 });
      return;
    }

    const now = new Date();
    const timeDiff = countdownEndTime.getTime() - now.getTime();

    if (timeDiff <= 0) {
      setTimeRemaining({ days: 0, hours: 0, mins: 0, secs: 0 });

      // Timer expired - toggle state
      // if (!isLocked) {
      //   // 14 days expired without submission, lock the routine
      //   setIsLocked(true);
      // } else {
      //   // 90 days expired, unlock for new revision window
      //   setIsLocked(false);
      // }
      setCountdownEndTime(null);
      return;
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const mins = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const secs = Math.floor((timeDiff % (1000 * 60)) / 1000);

    setTimeRemaining({ days, hours, mins, secs });
    setIsTimerActive(true);
  };

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    setShowChangeForm(true);
  };

  const handleSubmit = (values: any) => {
    console.log("survey_id", survey_id);
    setLoading(true);

    const data = {
      is_satisfied: selectedOption === "yes" ? 1 : 0,
      customer_response: values.customer_response || "",
    };

    dispatch(
      saveReviseRoutineApi({
        data,
        finalCallback: () => {
          setLoading(false);
          form.resetFields();
          setInputValue("");
        },
        successCallback: (apiResponse: any) => {
          setResponse(apiResponse?.data?.response);
          if (apiResponse.success) {
            // Update state based on API response
            const responseData = apiResponse.data;
            setIsAlreadyLocked(responseData.show_next_expert_review_button);
            setShowNextExpertReviewButton(false);
            const isSatisfied = responseData.is_satisfied;
            setSelectedOption(
              isSatisfied ? "yes" : isSatisfied === false ? "no" : ""
            );

            console.log(
              "responseData.routine_status",
              responseData.routine_status
            );
            // Lock routine after submission
            if (responseData.routine_status === "locked") {
              setIsLocked(true);
            } else if (responseData.routine_status === "unlocked") {
              setIsLocked(false);
            }
            setShowChangeForm(false);

            // Set countdown for 90 days based on revision_time_remaining
            if (responseData.revision_time_remaining) {
              const now = new Date();
              const lockEnd = new Date(
                now.getTime() + responseData.revision_time_remaining * 1000
              );
              setCountdownEndTime(lockEnd);
            }
          }
        },
        failureCallback: () => {
          setLoading(false);
          setError(true);
        },
      })
    );
  };

  const formatTime = (time: number) => {
    return time.toString().padStart(2, "0");
  };

  const getTimerHeading = () => {
    if (!isLocked) {
      return "Revision Window Ends In";
    } else {
      return "Next Expert Review Starts In";
    }
  };

  const getStatusMessage = () => {
    if (!isLocked) {
      return "You have 14 days to request any changes. After it closes, this routine will be locked until the next scheduled expert review.";
    } else {
      return "Your routine is currently locked. The button will be enabled when the next review period begins.";
    }
  };

  return (
    <section className="store-section revise-routine-section">
      <Card>
        <h1 className="tracker-title store-card__title">
          Are you satisfied with your expert-built skincare routine?
        </h1>

        <div className="option-buttons">
          <Button
            disabled={isLocked}
            onClick={() => handleOptionClick("yes")}
            className={`option-button btn-primary ${
              selectedOption === "yes" ? "option-selected" : ""
            }`}
          >
            Yes - I'm satisfied
            {selectedOption === "yes" && (
              <span className="button-sparkle">✨</span>
            )}
          </Button>
          <Button
            disabled={isLocked}
            onClick={() => handleOptionClick("no")}
            className={`option-button btn-primary ${
              selectedOption === "no" ? "option-selected" : ""
            }`}
          >
            No — I want changes
            {selectedOption === "no" && (
              <span className="button-sparkle">✨</span>
            )}
          </Button>
        </div>

        {selectedOption !== "" && (
          <div className="change-request-form">
            <Form form={form} onFinish={handleSubmit}>
              <Form.Item
                name="customer_response"
                rules={[
                  {
                    required: true,
                    message: "Please provide your feedback.",
                  },
                ]}
              >
                <div className="ingredient-checker__placeholder">
                  {!inputValue && (
                    <div className="ingredient-checker__placeholder-value">
                      {selectedOption === "yes" ? (
                        <p className="placeholder-des">
                          That’s great! We would love to hear from you: What did
                          you like the most about your routine?
                        </p>
                      ) : (
                        <>
                          <p className="placeholder-des">
                            What changes would you like the expert to make?
                          </p>
                          <p className="placeholder-des">
                            For example: Swap products or reduce skincare steps
                            or recommend cheaper option.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <TextArea
                    rows={6}
                    maxLength={600}
                    value={inputValue}
                    disabled={isLocked}
                    onChange={(e) => setInputValue(e.target.value)}
                  />
                </div>
              </Form.Item>

              {!isLocked && (
                <div
                  className="submit-btn-holder"
                  style={{ textAlign: "right" }}
                >
                  <Button
                    htmlType="submit"
                    className="submit-btn ant-btn btn-primary"
                    loading={loading}
                  >
                    {selectedOption === "yes"
                      ? "Lock This Routine"
                      : "Submit Change Request"}
                  </Button>
                </div>
              )}
            </Form>
          </div>
        )}
      </Card>

      <Card>
        <div className="status-timer-card">
          <div className="card-content">
            {/* Timer */}
            <div className="timer-section">
              <h3 className="timer-heading">{getTimerHeading()}</h3>
              {isTimerActive && (
                <div className="countdown-display">
                  <div className="time-unit">
                    <div className="time-value">
                      {formatTime(timeRemaining.days)}
                    </div>
                    <div className="time-label">Days</div>
                  </div>
                  <div className="time-unit">
                    <div className="time-value">
                      {formatTime(timeRemaining.hours)}
                    </div>
                    <div className="time-label">Hrs</div>
                  </div>
                  <div className="time-unit">
                    <div className="time-value">
                      {formatTime(timeRemaining.mins)}
                    </div>
                    <div className="time-label">Mins</div>
                  </div>
                  <div className="time-unit">
                    <div className="time-value">
                      {formatTime(timeRemaining.secs)}
                    </div>
                    <div className="time-label">Secs</div>
                  </div>
                </div>
              )}
            </div>
            {/* Status Message */}
            <div className="status-info">
              {isAlreadyLocked ? (
                <Button
                  className={!isLocked ? "btn-primary" : "btn-gray"}
                  disabled={isLocked}
                  style={{ width: "100%" }}
                  onClick={() => {
                    setIsLocked(false);
                    setShowChangeForm(false);
                    setSelectedOption("");
                    navigate("/skincaresteps");

                    ReactGA.event({
                      category: "Store",
                      action: "Request New Skincare Updates button clicked",
                      label: "",
                      nonInteraction: true,
                      transport: "xhr",
                    });
                  }}
                >
                  Request for New Skincare Updates
                </Button>
              ) : (
                <p className="status-text">{getStatusMessage()}</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="footer-cta">
     
          <Button
            className="btn-primary cta-btn"
            onClick={() => {
              navigate("/store/skin-solution");
              ReactGA.event({
                category: "Store",
                action: `Go to The Appreciate You button clicked`,
                label: "",
                nonInteraction: true, // optional, true/false
                transport: "xhr",
              });
            }}
          >
          Go to Skin Solution
          </Button>
      </div>
    </section>
  );
};

export default ReviseRoutineComponent;
